import { QueryClient } from "@tanstack/react-query";
import { isKontorError } from "@kontor/shared/errors";

/**
 * Query configuration.
 *
 * The brief is that the UI must feel instant. Most of that is achieved here rather
 * than in components: navigating back to a list the user saw a moment ago must paint
 * from cache with no spinner, while anything that could be stale revalidates behind
 * the paint.
 *
 * Staleness is tuned per resource in `staleTimes` below, because "how long may this
 * be out of date" is a domain question. A Liegenschaft's address changes yearly; the
 * open-items list changes whenever a payment lands.
 */

/** How long a resource stays fresh, in milliseconds, keyed by the first path segment. */
const staleTimes: Record<string, number> = {
  // Master data: edited rarely, read constantly.
  liegenschaften: 5 * 60_000,
  einheiten: 5 * 60_000,
  personen: 5 * 60_000,
  lieferanten: 5 * 60_000,
  stammdaten: 10 * 60_000,
  // Reference data changes when the law does.
  referenz: 60 * 60_000,
  // Transactional: revalidate quickly.
  vorschreibungen: 30_000,
  buchungen: 30_000,
  bank: 15_000,
  offeneposten: 15_000,
  schaeden: 30_000,
  fristen: 60_000,
  // Live surfaces manage their own refetching.
  agent: 0,
};

const DEFAULT_STALE_TIME = 60_000;

export function staleTimeFor(queryKey: readonly unknown[]): number {
  const head = queryKey[0];
  if (typeof head === "string" && head in staleTimes) {
    return staleTimes[head] ?? DEFAULT_STALE_TIME;
  }
  return DEFAULT_STALE_TIME;
}

export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: DEFAULT_STALE_TIME,
        // Keep painted data for a while after it goes unused, so going back to a
        // list is instant even after a detour through three other screens.
        gcTime: 15 * 60_000,
        refetchOnWindowFocus: true,
        refetchOnReconnect: true,
        // Placeholder data is unset by default; each list opts in with `keepPreviousData`
        // so paging does not blank the table.
        retry: (failureCount, error) => {
          // Client faults will not fix themselves — retrying a 403 just delays the
          // error the user needs to see.
          if (isKontorError(error) && error.httpStatus < 500 && error.code !== "rate_limited") {
            return false;
          }
          return failureCount < 2;
        },
        retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 8000),
      },
      mutations: {
        // Mutations are never retried automatically: releasing a payment twice
        // because a response was slow is not an acceptable failure mode.
        retry: false,
      },
    },
  });
}
